import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import { isPrerendering, fallbackBlogPosts } from '../prerenderData';
import './BlogPage.css';
import { apiFetch } from '../services/api';

function BlogPage() {
  // Seeded from the build-time snapshot on the client too, so hydration
  // adopts the pre-rendered list rather than rebuilding it from empty.
  const [posts, setPosts] = useState(fallbackBlogPosts);
  const [loading, setLoading] = useState(false);
  // /blog?tag=permits - the tag chips on each post link here. A tag view is
  // a filter of this page rather than a page of its own, so the canonical
  // stays /blog; the title and description still say which tag is showing.
  const [searchParams] = useSearchParams();
  const tag = (searchParams.get('tag') || '').trim();
  const tagLabel = tag ? tag.charAt(0).toUpperCase() + tag.slice(1) : '';
  const shown = tag
    ? posts.filter((p) => (p.tags || []).some((t) => String(t).toLowerCase() === tag.toLowerCase()))
    : posts;

  useEffect(() => {
    if (isPrerendering) return;

    apiFetch('/api/blog')
      .then(r => r.json())
      .then(data => { setPosts(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const formatDate = (d) => new Date(d).toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="blog-page">
      <SEO
        title={tag ? `${tagLabel} - Awning & Pergola Guides` : 'Awning & Pergola Tips, Ideas & Guides'}
        description={tag
          ? `Articles tagged "${tag}" from the ASG Shading blog - practical guides on retractable awnings, pergolas and outdoor living in Melbourne.`
          : 'Practical guides on retractable awnings, pergolas and outdoor living in Melbourne - permits, comparisons, design ideas and maintenance advice from ASG Shading.'}
        keywords="pergola blog, outdoor living tips, pergola design ideas, Melbourne outdoor renovation, pergola maintenance"
        canonical="/blog"
        jsonLd={[SEO.organizationSchema, SEO.breadcrumbSchema([{ name: 'Blog' }]), SEO.webPageSchema({ type: 'CollectionPage', name: 'ASG Shading Blog', description: 'Tips, inspiration and expert insights for your outdoor living project.', url: '/blog' })]}
      />
      {/* Hero */}
      <section className="bp-hero">
        <div className="bp-hero-bg"></div>
        <div className="bp-hero-content">
          <p className="bp-label">Insights & Ideas</p>
          <h1 className="bp-title">The ASG Blog</h1>
          <p className="bp-subtitle">Tips, inspiration, and industry insights for your outdoor living project.</p>
        </div>
      </section>

      <Breadcrumbs items={[{ name: 'Blog' }]} />

      {/* Posts */}
      <section className="bp-posts-section">
        {tag && (
          <div className="bp-filter">
            <p>Showing articles tagged <strong>{tag}</strong></p>
            <Link to="/blog" className="bp-filter-clear">Show all articles</Link>
          </div>
        )}
        {loading ? (
          <div className="bp-loading"><div className="bp-spinner"></div></div>
        ) : shown.length === 0 ? (
          <div className="bp-empty">
            <h2>{tag ? `No articles tagged "${tag}" yet` : 'No posts yet'}</h2>
            {tag
              ? <p><Link to="/blog" className="bp-filter-clear">See all articles</Link></p>
              : <p>We're working on some great content. Check back soon!</p>}
          </div>
        ) : (
          <div className="bp-grid">
            {shown.map((post, idx) => (
              <Link to={`/blog/${post.slug}`} className={`bp-card ${idx === 0 ? 'bp-card--featured' : ''}`} key={post._id}>
                {post.featured_image && (
                  <div className="bp-card-img">
                    <img src={post.featured_image} alt={post.title} width={800} height={450} loading="lazy" />
                  </div>
                )}
                <div className="bp-card-body">
                  {post.tags && post.tags.length > 0 && (
                    <div className="bp-card-tags">
                      {post.tags.slice(0, 3).map((tag, i) => (
                        <span className="bp-tag" key={i}>{tag}</span>
                      ))}
                    </div>
                  )}
                  <h2 className="bp-card-title">{post.title}</h2>
                  {post.excerpt && <p className="bp-card-excerpt">{post.excerpt}</p>}
                  <div className="bp-card-meta">
                    <span className="bp-card-date">{formatDate(post.published_at || post.createdAt)}</span>
                    <span className="bp-card-read">Read article →</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export default BlogPage;
